import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EnergiesService, Range, Inverter, PowersService, removeMaskIdFromDeviceList, removeEmptyDevicesFromDeviceList } from 'ngx-core-services';
import { map, switchMap, catchError, withLatestFrom, mapTo, first } from 'rxjs/operators';
import { of, from } from 'rxjs';

import {
  AppState,
  SystemsState,
  getSystemDefinitionDeviceIdsForTelemetries,
  getSystemDefinitionConfiguration,
  requestSystemDailyEnergies,
  requestSystemDailyPowers,
  selectedSystemMetadata,
  setSystemDailyEnergiesError,
  setSystemDailyPowersError,
  updateSystemDailyEnergies,
  updateSystemDailyPowers,
} from '..';
import { getDailyPowerTelemetry } from './effects.utils';

@Injectable()
export class DailyStateOverviewEffects {

  requestDailyEnergies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemDailyEnergies),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) => {
        return from(this.getDailyEnergies(
            action.systemId,
            action.range,
            action.forceRefresh,
            systemState,
          )
        ).pipe(
          map((dailyEnergies) => updateSystemDailyEnergies({ systemId: action.systemId,range: action.range, instanceId: action.instanceId, systemEnergyState: dailyEnergies })),
          catchError(_ => of(setSystemDailyEnergiesError({ systemId: action.systemId, instanceId: action.instanceId, error: 'Failed to fetch system energy state' })))
        );
      })
    )
  );

  requestDailyPowers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemDailyPowers),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) => {
        return from(this.getDailyPowers(
            action.systemId,
            action.range,
            action.forceRefresh,
            systemState,
          )
        ).pipe(
          map((powers) => updateSystemDailyPowers({ systemId: action.systemId, instanceId: action.instanceId, range: action.range, systemPowerState: powers })),
          catchError(_ => of(setSystemDailyPowersError({ systemId: action.systemId, instanceId: action.instanceId, error: 'Failed to fetch system energy state' })))
        );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly energiesService: EnergiesService,
    private readonly powersService: PowersService,
  ) {}

  async getDailyEnergies(systemId: string, range: Range, forceRefresh: boolean, state: SystemsState): Promise<Partial<Inverter>> {
    const system = state.systemList[systemId];

    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);
    const serials = getSystemDefinitionDeviceIdsForTelemetries(system);
    const energies = await this.energiesService.fetchEnergiesByRange(serials, systemConfiguration.monitoringType, range, forceRefresh);
    const rawTelemetry: { [key: string]: Partial<Inverter> } = {};

    Object.keys(energies).forEach((key) => {
      if (energies[key] && energies[key][0] !== null && energies[key][0] !== undefined) {
          rawTelemetry[key] = energies[key][0];
      } else {
          rawTelemetry[key] = {};
      }
    });
    return this.energiesService.getSnapshotEnergyAggregate(rawTelemetry, !!systemConfiguration.inverterMeterSensor);
  }

  async getDailyPowers(systemId: string, range: Range, forceRefresh: boolean, state: SystemsState): Promise<Partial<Inverter>[]> {
    const system = state.systemList[systemId];

    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);
    const serials = getSystemDefinitionDeviceIdsForTelemetries(system);
    const powers = await this.powersService.fetchDevicesPowerTelemetryByRange(serials, systemConfiguration.monitoringType, range.from, forceRefresh, `${systemConfiguration.tz}`);
    if ( systemConfiguration.monitoringType === 'Connext' || systemConfiguration.monitoringType === 'AzzurroHub') {
      const connextKey = Object.keys(powers).length > 0 ? Object.keys(powers)[0] : '';
      return powers[connextKey];
    }
    return getDailyPowerTelemetry(
      removeEmptyDevicesFromDeviceList(removeMaskIdFromDeviceList(powers)),
      new Date(range.from),
      systemConfiguration.tz,
      systemConfiguration.structure,
      !!systemConfiguration.inverterMeterSensor,
    ).pipe(first()).toPromise();
  }
}
